import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  FeatureFlagEntry,
  JsonEnvelope,
  ReloadResult,
  RevertResult,
  UpdateFeatureFlagResult,
} from '@/client/types/feature-flags';
import { featureFlagKeys } from './feature-flags.query';

// ─── Create / Update override ─────────────────────────────────────────────────
const useUpdateFeatureFlagFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (name: string, entry: FeatureFlagEntry): Promise<UpdateFeatureFlagResult> => {
      const res = await request<JsonEnvelope<UpdateFeatureFlagResult>>(
        `/api/admin/feature-flags/update-feature-flag/${encodeURIComponent(name)}`,
        'POST',
        entry,
      );
      return res.data;
    },
    'Failed to save the feature flag',
  );
};

export const useUpdateFeatureFlagMutation = () => {
  const queryClient = useQueryClient();
  const updateFn = useUpdateFeatureFlagFn();
  return useMutation({
    mutationFn: ({ name, entry }: { name: string; entry: FeatureFlagEntry }) =>
      updateFn(name, entry),
    onSuccess: () => {
      // Reconcile against `/list` — the write is instant for the serving worker
      // and global within ~one poll interval, so re-fetch to converge.
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.list() });
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.all });
    },
  });
};

// ─── Revert override → committed default ───────────────────────────────────────
// Only valid for `source: "override"` rows.
// Errors: 404 `override_not_found`, 409 `db_store_disabled`.

const useRevertFeatureFlagFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (name: string): Promise<RevertResult> => {
    const res = await request<JsonEnvelope<RevertResult>>(
      `/api/admin/feature-flags/override/${encodeURIComponent(name)}`,
      'DELETE',
    );
    return res.data;
  }, 'Failed to revert the feature flag to its default');
};

export const useRevertFeatureFlagMutation = () => {
  const queryClient = useQueryClient();
  const revertFn = useRevertFeatureFlagFn();
  return useMutation({
    mutationFn: ({ name }: { name: string }) => revertFn(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.list() });
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.all });
    },
  });
};

// ─── Force-refresh one serving worker (advanced) ───────────────────────────────
// Rarely needed — the poller already fans changes out to all workers within one
// interval. Offer only as an "advanced / force refresh" affordance.

const useReloadFeatureFlagsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (): Promise<ReloadResult> => {
    const res = await request<JsonEnvelope<ReloadResult>>(
      '/api/admin/feature-flags/reload-feature-flags',
      'POST',
      {},
    );
    return res.data;
  }, 'Failed to reload feature flags');
};

export const useReloadFeatureFlagsMutation = () => {
  const queryClient = useQueryClient();
  const reloadFn = useReloadFeatureFlagsFn();
  return useMutation({
    mutationFn: () => reloadFn(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.list() });
    },
  });
};
